/**
 * Phone number input — a single floating-label field combining a dial-code selector and the number
 * input in one bordered box (rather than two separate controls). Stores and returns the full
 * international number (e.g. "+1 5551234567").
 *
 * Booking accepts any guest's phone (not just the profile owner's), so the selector offers the whole
 * shared dial-code list (`@elara/client-core/lib/dial-codes`: US/CA and UK first, then alphabetical)
 * rather than only the profile's country. The trigger shows the flag and compact dial code while a
 * transparent native `<select>` overlaid on it lists the full country names, matching mobile's picker.
 */
import { useEffect, useState } from 'react';

import { DIAL_CODES, DIAL_CODES_BY_LENGTH, flagEmoji } from '@elara/client-core/lib/dial-codes';
import { cn } from '@elara/client-core/lib/utils';

import { FloatingField } from './floating-field.js';
import { IconSizes, MaterialIcon } from './MaterialIcon.js';

/**
 * Parse a full phone string into dial code + national number. Handles both the form's own
 * "+1 5551234567" shape AND a bare E.164 like "+15551234567" (e.g. the verified Firebase Auth phone).
 * The previous greedy `\+\d{1,3}` regex grabbed "+155" for a US number, failed the exact-code lookup, and
 * dumped the whole "+1…" string into the national field — doubling the country code. Now we strip
 * whitespace and take the LONGEST dial code that prefixes the value.
 */
export function parsePhone(value: string): { dialCode: string; number: string } {
  const compact = value.replaceAll(/\s+/g, '');
  if (!compact) return { dialCode: '+1', number: '' };
  if (compact.startsWith('+')) {
    const matched = DIAL_CODES_BY_LENGTH.find((d) => compact.startsWith(d.code));
    if (matched) return { dialCode: matched.code, number: compact.slice(matched.code.length) };
  }
  return { dialCode: '+1', number: value.trim() };
}

export interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  id?: string;
  disabled?: boolean;
  /** Hide the "Phone number" label and drop the space reserved for it, when the context already says it. */
  hideLabel?: boolean;
}

export function PhoneInput({ value, onChange, id, disabled, hideLabel }: PhoneInputProps) {
  const parsed = parsePhone(value);
  const [dialCode, setDialCode] = useState(parsed.dialCode);
  const [number, setNumber] = useState(parsed.number);

  // Sync when external value changes
  useEffect(() => {
    const p = parsePhone(value);
    setDialCode(p.dialCode);
    setNumber(p.number);
  }, [value]);

  const emit = (code: string, num: string) => {
    const trimmed = num.trim();
    onChange(trimmed ? `${code} ${trimmed}` : '');
  };

  const handleNumberChange = (raw: string) => {
    // Browser autofill or a paste can drop a FULL international number into this field. Re-parse it into
    // the dial-code + national split instead of concatenating, which would otherwise double the country
    // code (e.g. "+1" + "+1 267…").
    if (raw.trimStart().startsWith('+')) {
      const p = parsePhone(raw);
      setDialCode(p.dialCode);
      setNumber(p.number);
      onChange(p.number.trim() ? `${p.dialCode} ${p.number}` : '');
      return;
    }
    setNumber(raw);
    emit(dialCode, raw);
  };

  const selected = DIAL_CODES.find((d) => d.code === dialCode);
  const placeholder = selected?.placeholder ?? '';
  // With the label shown, controls sit low to clear the floated label band; without it, they center.
  const controlPadding = hideLabel ? 'py-0' : 'pt-5 pb-1';

  return (
    <FloatingField
      label="Phone number"
      htmlFor={id}
      filled={number !== ''}
      forceFloat
      hideLabel={hideLabel}
      disabled={disabled}
    >
      <div className="flex h-12 items-stretch">
        {/* Dial-code selector: flag + compact code + chevron with a transparent native <select> overlaid, so the
            trigger stays narrow while the dropdown lists full country names. It never shrinks or wraps: in a
            narrow dialog the number input beside it gives way instead, or the flag and code split onto two lines. */}
        <div
          className={cn(
            'relative flex shrink-0 items-center gap-0.5 pl-3 pr-2 text-base text-foreground',
            controlPadding,
          )}
        >
          <span className="whitespace-nowrap">{selected ? `${flagEmoji(selected.iso)} ${dialCode}` : dialCode}</span>
          <MaterialIcon icon="expand_more" size={IconSizes.sm} className="opacity-50" />
          <select
            aria-label="Country dial code"
            value={dialCode}
            disabled={disabled}
            onChange={(e) => {
              setDialCode(e.target.value);
              emit(e.target.value, number);
            }}
            className="absolute inset-0 cursor-pointer opacity-0 disabled:cursor-not-allowed"
          >
            {DIAL_CODES.map((d) => (
              <option key={d.code} value={d.code}>
                {`${flagEmoji(d.iso)} ${d.name} (${d.code})`}
              </option>
            ))}
          </select>
        </div>

        {/* Divider between the code selector and the number — sits in the value row, clear of the
            floated label band at the top. */}
        <div className={cn('w-px shrink-0 bg-input', hideLabel ? 'my-3' : 'mt-5.5 mb-1.5')} />

        <input
          id={id}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          value={number}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(e) => handleNumberChange(e.target.value)}
          className={cn(
            'h-full min-w-0 flex-1 border-0 bg-transparent px-3 text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-0 disabled:cursor-not-allowed',
            controlPadding,
          )}
        />
      </div>
    </FloatingField>
  );
}
